import { LocalProductRef, ProductLimits } from 'src/app/models/product-limits.model';
import { normalizeCode } from './code-quantities';

/**
 * Resolve a productLimits entry to its recipe: by type + id when the list is
 * typed, otherwise by bare id (last one wins, as it always did).
 */
export function localProductLookup(
  localProducts: LocalProductRef[]
): (productId: number, type: string) => LocalProductRef | undefined {
  const typed = new Map<string, LocalProductRef>();
  const untyped = new Map<number, LocalProductRef>();
  (localProducts || []).forEach(product => {
    if (product.type) {
      typed.set(`${product.type}:${product.id}`, product);
    } else {
      untyped.set(product.id, product);
    }
  });
  return (productId, type) => typed.get(`${type}:${productId}`) ?? untyped.get(productId);
}

/**
 * Local-product recipes (CQ/LQ/EQ/TCQ) are matched by subset: every recipe
 * whose category-content set is fully contained in the current selection
 * fires. The admin authors one recipe per combination of options, so for a
 * door with a drop seal and a closer selected, the {door}, {door+seal},
 * {door+closer} and {door+seal+closer} recipes all match — and each becomes
 * its own card and its own addend in the PDF quantities. Only the last one is
 * correct; the others are less-specific drafts of the same thing.
 *
 * This drops a recipe when another matched recipe of the same kind shares at
 * least one output code with it and either
 *   - is strictly more specific: it covers all of this recipe's
 *     category-contents AND tool types and names strictly more of them, or
 *   - is an exact duplicate (same category-contents and tool types) with a
 *     higher id — the newest of identical recipes wins.
 *
 * Tool types matter for the "Tool Forms" (ToolCode) recipes: an
 * ALUNA + DIN LEFT recipe and an ALUNA + DIN RIGHT recipe share the ALUNA
 * category but neither subsumes the other, and a bare ALUNA + DC04 recipe
 * (no tool) must not silently swallow the opening-position line.
 *
 * The shared-code condition is deliberate. Two recipes bound to the same
 * options but producing unrelated codes (e.g. the profile kit vs. the glass
 * for the same door) are both legitimate and must both survive; only recipes
 * that are genuinely the same line at different specificity are collapsed.
 *
 * A recipe whose categories are unknown (not in `localProducts` — an old cart
 * snapshot, say) is always kept: with nothing to compare it degrades to the
 * previous behaviour rather than dropping a line.
 *
 * Mirrored in ProjectConfiguration::prunedProductLimits() on the backend.
 * Pure and idempotent: pruning an already-pruned list is a no-op.
 */
export function pruneSubsumedProductLimits(
  productLimits: ProductLimits[],
  localProducts: LocalProductRef[]
): ProductLimits[] {
  const list = productLimits || [];
  if (list.length < 2) { return list; }

  const lookup = localProductLookup(localProducts);

  const meta = list.map(entry => {
    const product = lookup(entry.productId, entry.type);
    return {
      entry,
      categories: product ? new Set(product.categories || []) : null,
      toolTypes: new Set(product?.toolTypes || []),
      codes: outputCodes(entry),
    };
  });

  return meta
    .filter(candidate => {
      const candidateCategories = candidate.categories;
      // Unknown category set -> nothing to reason about, keep it.
      if (!candidateCategories || candidate.codes.size === 0) { return true; }

      const subsumed = meta.some(other => {
        if (other === candidate || !other.categories) { return false; }
        if (other.entry.type !== candidate.entry.type) { return false; }
        if (!shareAnyCode(other.codes, candidate.codes)) { return false; }

        if (!isSubset(candidateCategories, other.categories)) { return false; }
        if (!isSubset(candidate.toolTypes, other.toolTypes)) { return false; }

        const otherSize = other.categories.size + other.toolTypes.size;
        const candidateSize = candidateCategories.size + candidate.toolTypes.size;
        if (otherSize > candidateSize) { return true; }
        // Covers it and is not larger -> identical sets: keep the newest.
        return other.entry.productId > candidate.entry.productId;
      });

      return !subsumed;
    })
    .map(candidate => candidate.entry);
}

/** Every member of `small` is in `big` (equal sets included). */
function isSubset(small: Set<number>, big: Set<number>): boolean {
  for (const id of small) {
    if (!big.has(id)) { return false; }
  }
  return true;
}

/** Every code the recipe's resolved lines carry (LQ: `codeLimit`, others: `code`). */
function outputCodes(entry: ProductLimits): Set<string> {
  const codes = new Set<string>();
  (entry.limits || []).forEach((line: any) => {
    const code = normalizeCode(line.codeLimit ?? line.code);
    if (code) { codes.add(code); }
  });
  return codes;
}

function shareAnyCode(a: Set<string>, b: Set<string>): boolean {
  for (const code of a) {
    if (b.has(code)) { return true; }
  }
  return false;
}
